import { ADDITIONAL_SUB_TYPES, ImageTypeKeys, ImageTypeSettings, WINDSHIELD_SUB_TYPES } from '../workflows/workflows';
import { GENERIC_STATE_KEYS } from '../root';

export interface IPhotoSeries {
  id: string;
  vehicleType: number;
  vehicle: IVehicle;
  imageTypeSettings: ImageTypeSettings[];
  smartScanImageDownloadEnabled: boolean;
  trackGeolocationEnabled: boolean;
  trackGeolocationRequired: boolean;
  expectedCountryGeolocationRequired: boolean;
  reportSigningRequired: boolean;
  userFeedbackSettings: IUserFeedbackSettings;
  windshieldRepairReplaceDecisionEnabled: boolean;
  windshieldRepairReplaceDecision: WindshieldRepairReplaceDecision | undefined;
  automatedWindshieldCalculationInProgress: boolean;
  smartScanNativeCameraEnabled: boolean;
  requestRegistrationNo: boolean;
  smartScanUserConsentEnabled: boolean;
  trackDeviceOrientationEnabled: boolean;
  addWindshieldRepairDecisionToReturnUrl: boolean;
  smartScanWindshieldRepairSummaryEnabled: boolean;
  additionalCloseUpsOnly: boolean;
  isCaptureOnlyPhotosOfAdditionalImages: boolean;
  damageMarkingType: DamageMarkingType;
  damageTypeSelectionType: DamageTypeSelectionType;
  customDamagesEnabled: boolean;
  crackRepairEnabled: boolean;
  aiLocationAssistanceEnabled: boolean;
}

export interface IUserFeedbackSettings {
  satisfactionRatingEnabled: boolean;
  commentEnabled: boolean;
}

export enum WindshieldRepairReplaceDecision {
  Repair = 0,
  Replace = 1,
  Uncertain = 2,
  NoActionRequired = 3,
  Calculating = 10
}

export enum DamageMarkingType {
  Off = 0,
  // User is marking manually damages
  Manual = 1,
  // AI decides damage location
  AI = 2
}

export enum DamageTypeSelectionType {
  Off = 0,
  // User manually selects damage type
  Manual = 1,
  // Damage type is determined by AI
  AI = 2
}

export class PhotoSeries implements IPhotoSeries {
  constructor(photoSeries: IPhotoSeries) {
    this.id = photoSeries.id;
    this.vehicleType = photoSeries.vehicleType;
    this.vehicle = photoSeries.vehicle;
    this.imageTypeSettings = photoSeries.imageTypeSettings;
    this.smartScanImageDownloadEnabled = photoSeries.smartScanImageDownloadEnabled;
    this.trackGeolocationEnabled = photoSeries.trackGeolocationEnabled;
    this.trackGeolocationRequired = photoSeries.trackGeolocationRequired;
    this.expectedCountryGeolocationRequired = photoSeries.expectedCountryGeolocationRequired;
    this.reportSigningRequired = photoSeries.reportSigningRequired;
    this.userFeedbackSettings = photoSeries.userFeedbackSettings;
    this.windshieldRepairReplaceDecisionEnabled = photoSeries.windshieldRepairReplaceDecisionEnabled;
    this.windshieldRepairReplaceDecision = photoSeries.windshieldRepairReplaceDecision;
    this.smartScanNativeCameraEnabled = photoSeries.smartScanNativeCameraEnabled;
    this.requestRegistrationNo = photoSeries.requestRegistrationNo;
    this.smartScanUserConsentEnabled = photoSeries.smartScanUserConsentEnabled;
    this.trackDeviceOrientationEnabled = photoSeries.trackDeviceOrientationEnabled;
    this.addWindshieldRepairDecisionToReturnUrl = photoSeries.addWindshieldRepairDecisionToReturnUrl;
    this.smartScanWindshieldRepairSummaryEnabled = photoSeries.smartScanWindshieldRepairSummaryEnabled;
    this.automatedWindshieldCalculationInProgress = photoSeries.automatedWindshieldCalculationInProgress; // TODO: REFACTOR this out
    this.additionalCloseUpsOnly = photoSeries.additionalCloseUpsOnly;
    this.isCaptureOnlyPhotosOfAdditionalImages = photoSeries.isCaptureOnlyPhotosOfAdditionalImages;
    this.damageMarkingType = photoSeries.damageMarkingType;
    this.damageTypeSelectionType = photoSeries.damageTypeSelectionType;
    this.customDamagesEnabled = photoSeries.customDamagesEnabled;
    this.crackRepairEnabled = photoSeries.crackRepairEnabled;
    this.aiLocationAssistanceEnabled = photoSeries.aiLocationAssistanceEnabled;
  }

  public id: string;

  public vehicleType: number;

  public vehicle: IVehicle;

  public imageTypeSettings: ImageTypeSettings[];

  public smartScanImageDownloadEnabled: boolean;

  public trackGeolocationEnabled: boolean;

  public trackGeolocationRequired: boolean;

  public expectedCountryGeolocationRequired: boolean;

  public reportSigningRequired: boolean;

  public userFeedbackSettings: IUserFeedbackSettings;

  public windshieldRepairReplaceDecisionEnabled: boolean;

  public windshieldRepairReplaceDecision: WindshieldRepairReplaceDecision | undefined;

  public smartScanUserConsentEnabled: boolean;

  public trackDeviceOrientationEnabled: boolean;

  public addWindshieldRepairDecisionToReturnUrl: boolean;

  public smartScanWindshieldRepairSummaryEnabled: boolean;

  public automatedWindshieldCalculationInProgress: boolean;

  public additionalCloseUpsOnly: boolean;

  public isCaptureOnlyPhotosOfAdditionalImages: boolean;

  public damageMarkingType: DamageMarkingType;

  public damageTypeSelectionType: DamageTypeSelectionType;

  public customDamagesEnabled: boolean;

  public crackRepairEnabled: boolean;

  public aiLocationAssistanceEnabled: boolean;

  /// BEGIN: Getter properties ///
  public get manualDamageLabellingEnabled() {
    return this.damageMarkingType === DamageMarkingType.Manual;
  }

  public get manualDamageLabellingCustomDamagesEnabled() {
    return this.manualDamageLabellingEnabled && this.customDamagesEnabled;
  }

  public get manualDamageLabellingCrackRepairEnabled() {
    return this.manualDamageLabellingEnabled && this.crackRepairEnabled;
  }

  public get manualLabellingWindshieldDamageDetection() {
    return this.manualDamageLabellingEnabled && this.damageTypeSelectionType === DamageTypeSelectionType.AI;
  }
  /// END: Getter properties ///

  public isManualDamageLabellingEnabled = (imageType: number, imageSubType: number | undefined, customImageTypeId: string | null): boolean => {
    if (!this.manualDamageLabellingEnabled) return false;

    // Do not allow manual marking if additional close ups of damages
    const isAdditionalCloseUps = imageType === ImageTypeKeys.Additional
      && (imageSubType === ADDITIONAL_SUB_TYPES.FIRST_CLOSE_UP || imageSubType === ADDITIONAL_SUB_TYPES.SECOND_CLOSE_UP);

    if (isAdditionalCloseUps) return false;

    // Do not allow manual marking if windshield factory label
    if (imageType === ImageTypeKeys.Windshield && imageSubType === WINDSHIELD_SUB_TYPES.FACTORY_LABEL) {
      return false;
    }

    const imageTypeSettings = this.imageTypeSettings
      .find((settings) => settings.imageType === imageType && settings.customImageTypeId === customImageTypeId);

    return imageTypeSettings !== undefined
      ? imageTypeSettings.manualDamageLabelling.enabled
      : false;
  };

  public smartScanNativeCameraEnabled: boolean;

  public requestRegistrationNo: boolean;
}

interface IVehicle {
  id: string,
  registrationNo: string,
  vin: string
}

export const PHOTOSERIES_NAME = 'photoSeries';

export const PHOTOSERIES_STATE_KEYS = {
  ...GENERIC_STATE_KEYS,
  photoSeries: 'photoSeries',
};
